import { useMemo, useState, JSX } from 'react';
import {
  Button,
  Paper,
  Stack,
  Text,
  Group,
  Badge,
  Collapse,
  ActionIcon,
} from '@mantine/core';
import {
  IconPlus,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient, Encounter, Procedure } from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { useTherapeuticServiceActions } from '@hooks/useTherapeuticServiceActions';
import { ConfirmCloseDialog } from '@/components/ConfirmCloseDialog';
import {
  createNewTherapeuticServiceProcedure,
  setServiceCode,
  setStartDate,
  setEndDate,
  setResponse,
  getContextEncounterId,
  setStatus,
} from '@/utils/procedure.utils';
import {
  TherapeuticServiceModal,
  TherapeuticServiceFormData,
} from './TherapeuticServiceModal';
import { TherapeuticServiceListItem } from './TherapeuticServiceListItem';
import { StopTherapeuticServiceModal } from './StopTherapeuticServiceModal';

export interface TherapeuticServiceListProps {
  services: TherapeuticServiceFormData[];
  onChange: (services: TherapeuticServiceFormData[]) => void;
  currentEncounterId?: string;
  patient: Patient;
  encounter: Encounter;
}

export const TherapeuticServiceList = ({
  services,
  onChange,
  currentEncounterId,
  patient,
  encounter,
}: TherapeuticServiceListProps): JSX.Element => {
  const { t } = useTranslation(['patient-pages']);
  const { saveProcedure, stopProcedure, deleteProcedure } =
    useTherapeuticServiceActions(patient, encounter);

  const [modalOpened, setModalOpened] = useState(false);
  const [stopModalOpened, setStopModalOpened] = useState(false);
  const [editingService, setEditingService] = useState<
    TherapeuticServiceFormData | undefined
  >();
  const [editingIndex, setEditingIndex] = useState<number | undefined>();
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Section collapse states
  const [stoppedThisEncounterCollapsed, setStoppedThisEncounterCollapsed] =
    useState(false);
  const [previouslyStoppedCollapsed, setPreviouslyStoppedCollapsed] =
    useState(true);

  //Group services by status and encounter
  const { activeServices, stoppedThisEncounter, previouslyStopped } =
    useMemo(() => {
      const active: { service: TherapeuticServiceFormData; index: number }[] =
        [];
      const stoppedThis: {
        service: TherapeuticServiceFormData;
        index: number;
      }[] = [];
      const stoppedPrev: {
        service: TherapeuticServiceFormData;
        index: number;
      }[] = [];

      services.forEach((service, index) => {
        const serviceIsStopped = service.status === 'stopped';
        const encounterId = service.encounterId;

        if (!serviceIsStopped) {
          active.push({ service, index });
        } else if (currentEncounterId && encounterId === currentEncounterId) {
          stoppedThis.push({ service, index });
        } else {
          stoppedPrev.push({ service, index });
        }
      });

      return {
        activeServices: active,
        stoppedThisEncounter: stoppedThis,
        previouslyStopped: stoppedPrev,
      };
    }, [services, currentEncounterId]);

  // Handle add button click
  const handleAdd = () => {
    setEditingService(undefined);
    setModalMode('add');
    setModalOpened(true);
  };

  // Handle edit action from list item
  const handleEdit = (service: TherapeuticServiceFormData, index: number) => {
    setEditingService(service);
    setEditingIndex(index);
    setModalMode('edit');
    setModalOpened(true);
  };

  // Handle stop action from list item
  const handleStop = (service: TherapeuticServiceFormData, index: number) => {
    setEditingService(service);
    setEditingIndex(index);
    setStopModalOpened(true);
  };

  // Handle delete click - show confirmation dialog
  const handleDeleteClick = (id: string) => {
    setPendingDeleteId(id);
    setDeleteConfirmOpened(true);
  };

  // Handle confirmed delete action
  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) {
      return;
    }

    setDeleteConfirmOpened(false);
    setDeletingId(pendingDeleteId);

    try {
      await deleteProcedure(pendingDeleteId);

      // Update local state
      const updated = services.filter((s) => s.id !== pendingDeleteId);
      onChange(updated);

      notifications.show({
        color: 'green',
        title: t('therapeuticServices.notifications.deleteSuccess', {
          defaultValue: 'Service deleted',
        }),
        message: t('therapeuticServices.notifications.deleteSuccessMessage', {
          defaultValue: 'Therapeutic service removed successfully',
        }),
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: t('therapeuticServices.notifications.deleteError', {
          defaultValue: 'Failed to delete',
        }),
        message: String(error),
      });
    } finally {
      setDeletingId(null);
      setPendingDeleteId(null);
    }
  };

  // Handle cancel delete action
  const handleCancelDelete = () => {
    setDeleteConfirmOpened(false);
    setPendingDeleteId(null);
  };

  // Handle save from modal
  const handleSave = async (formData: TherapeuticServiceFormData) => {
    try {
      // Build Procedure resource
      let procedure: Procedure = createNewTherapeuticServiceProcedure(
        patient,
        encounter
      );

      // Set fields
      if (formData.service) {
        procedure = setServiceCode(procedure, formData.service);
      }
      procedure = setStartDate(procedure, formData.startDate);
      procedure = setEndDate(procedure, formData.terminationDate);
      procedure = setResponse(procedure, formData.response || undefined);
      // Note that we fallback to 'in-progress' since the form defaults to it
      procedure = setStatus(procedure, formData.status || 'in-progress');

      // If editing, preserve the ID and original encounter
      if (formData.id) {
        procedure = { ...procedure, id: formData.id };
        // Preserve original encounter if available
        if (formData.encounterId) {
          procedure = {
            ...procedure,
            encounter: { reference: `Encounter/${formData.encounterId}` },
          };
        }
      }

      // Save via hook
      const saved = await saveProcedure(procedure);

      // Update local state
      const savedFormData: TherapeuticServiceFormData = {
        id: saved.id,
        service: formData.service,
        startDate: formData.startDate,
        terminationDate: formData.terminationDate,
        response: formData.response,
        status: saved.status,
        encounterId: getContextEncounterId(saved),
      };

      let updated: TherapeuticServiceFormData[];
      if (formData.id) {
        // Update existing
        updated = services.map((s) =>
          s.id === formData.id ? savedFormData : s
        );
      } else {
        // Add new
        updated = [...services, savedFormData];
      }
      onChange(updated);

      notifications.show({
        color: 'green',
        title: t('therapeuticServices.notifications.saveSuccess', {
          defaultValue: 'Service saved',
        }),
        message: t('therapeuticServices.notifications.saveSuccessMessage', {
          defaultValue: 'Therapeutic service saved successfully',
        }),
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: t('therapeuticServices.notifications.saveError', {
          defaultValue: 'Failed to save',
        }),
        message: String(error),
      });
    }
  };

  // Handle stop from modal
  const handleStopService = async (
    stoppedProcedure: Procedure
  ): Promise<void> => {
    if (editingIndex === undefined || !editingService) {
      return;
    }

    // Convert Procedure back to FormData
    const stoppedFormData: TherapeuticServiceFormData = {
      id: stoppedProcedure.id,
      service: stoppedProcedure.code,
      startDate: stoppedProcedure.performedPeriod?.start
        ? new Date(stoppedProcedure.performedPeriod.start)
        : null,
      terminationDate: stoppedProcedure.performedPeriod?.end
        ? new Date(stoppedProcedure.performedPeriod.end)
        : null,
      response: stoppedProcedure.outcome?.text || '',
      status: stoppedProcedure.status,
      encounterId: getContextEncounterId(stoppedProcedure),
    };

    // Update local state
    const updated = services.map((s, i) =>
      i === editingIndex ? stoppedFormData : s
    );
    onChange(updated);
  };

  // Section header component
  const SectionHeader = ({
    title,
    count,
    collapsed,
    onToggle,
    color = 'gray',
  }: {
    title: string;
    count: number;
    collapsed: boolean;
    onToggle: () => void;
    color?: string;
  }) => (
    <Group gap="xs" style={{ cursor: 'pointer' }} onClick={onToggle} mb="xs">
      <ActionIcon variant="subtle" size="xs" color={color}>
        {collapsed ? (
          <IconChevronRight size={14} />
        ) : (
          <IconChevronDown size={14} />
        )}
      </ActionIcon>
      <Text fw={600} size="sm" c={color}>
        {title}
      </Text>
      <Badge size="xs" variant="light" color={color}>
        {count}
      </Badge>
    </Group>
  );

  return (
    <Stack gap="md">
      {/* Header */}
      <Text fw={600}>
        {t('therapeuticServices.title', {
          defaultValue: 'Therapeutic Services',
        })}
      </Text>

      {/* Empty state */}
      {services.length === 0 ? (
        <>
          <Paper withBorder radius="md" p="xl" bg="gray.0">
            <Text size="sm" c="dimmed" ta="center">
              {t('therapeuticServices.empty', {
                defaultValue: 'No therapeutic services recorded',
              })}
            </Text>
          </Paper>
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            size="sm"
            onClick={handleAdd}
          >
            {t('therapeuticServices.addButton', {
              defaultValue: 'Add Service',
            })}
          </Button>
        </>
      ) : (
        <>
          {/* Active Services */}
          {activeServices.length > 0 && (
            <Stack gap="xs">
              <Group gap="xs">
                <Text fw={600} size="sm">
                  {t('therapeuticServices.sections.active', {
                    defaultValue: 'Active Services',
                  })}
                </Text>
                <Badge size="xs" variant="light" mt={3}>
                  {activeServices.length}
                </Badge>
              </Group>
              {activeServices.map(({ service, index }) => (
                <TherapeuticServiceListItem
                  key={service.id || `active-${index}`}
                  service={service}
                  patient={patient}
                  onAction={(action) => {
                    if (action === 'edit') {
                      handleEdit(service, index);
                    }
                    if (action === 'stop' && service.id) {
                      handleStop(service, index);
                    }
                    if (action === 'delete' && service.id) {
                      handleDeleteClick(service.id);
                    }
                  }}
                  isDeleting={deletingId === service.id}
                />
              ))}
            </Stack>
          )}

          {/* Add Service Button */}
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            size="sm"
            onClick={handleAdd}
          >
            {t('therapeuticServices.addButton', {
              defaultValue: 'Add Service',
            })}
          </Button>

          {/* Stopped This Encounter */}
          {stoppedThisEncounter.length > 0 && (
            <Stack gap="xs">
              <SectionHeader
                title={t('therapeuticServices.sections.stoppedThisEncounter', {
                  defaultValue: 'Stopped This Encounter',
                })}
                count={stoppedThisEncounter.length}
                collapsed={stoppedThisEncounterCollapsed}
                onToggle={() =>
                  setStoppedThisEncounterCollapsed(
                    !stoppedThisEncounterCollapsed
                  )
                }
                color="red"
              />
              <Collapse in={!stoppedThisEncounterCollapsed}>
                <Stack gap="xs">
                  {stoppedThisEncounter.map(({ service, index }) => (
                    <TherapeuticServiceListItem
                      key={service.id || `stopped-${index}`}
                      service={service}
                      patient={patient}
                      onAction={(action) => {
                        if (action === 'edit') {
                          handleEdit(service, index);
                        }
                        if (action === 'delete' && service.id) {
                          handleDeleteClick(service.id);
                        }
                      }}
                      isDeleting={deletingId === service.id}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          )}

          {/* Previously Stopped */}
          {previouslyStopped.length > 0 && (
            <Stack gap="xs">
              <SectionHeader
                title={t('therapeuticServices.sections.previouslyStopped', {
                  defaultValue: 'Previously Stopped',
                })}
                count={previouslyStopped.length}
                collapsed={previouslyStoppedCollapsed}
                onToggle={() =>
                  setPreviouslyStoppedCollapsed(!previouslyStoppedCollapsed)
                }
                color="gray"
              />
              <Collapse in={!previouslyStoppedCollapsed}>
                <Stack gap="xs">
                  {previouslyStopped.map(({ service, index }) => (
                    <TherapeuticServiceListItem
                      key={service.id || `prev-stopped-${index}`}
                      service={service}
                      patient={patient}
                      onAction={(action) => {
                        if (action === 'edit') {
                          handleEdit(service, index);
                        }
                        if (action === 'delete' && service.id) {
                          handleDeleteClick(service.id);
                        }
                      }}
                      isDeleting={deletingId === service.id}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          )}
        </>
      )}

      {/* Modals */}
      <TherapeuticServiceModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onSave={handleSave}
        service={editingService}
        mode={modalMode}
      />

      <StopTherapeuticServiceModal
        opened={stopModalOpened}
        onClose={() => setStopModalOpened(false)}
        onConfirm={handleStopService}
        onStopToServer={async (
          procedure: Procedure,
          endDate: Date,
          reason?: string
        ) => {
          const stopped = await stopProcedure(procedure, endDate, reason);
          return stopped;
        }}
        procedure={
          editingService?.id
            ? {
                resourceType: 'Procedure',
                id: editingService.id,
                status: editingService.status || 'completed',
                subject: { reference: `Patient/${patient.id}` },
                encounter: editingService.encounterId
                  ? { reference: `Encounter/${editingService.encounterId}` }
                  : { reference: `Encounter/${encounter.id}` },
                code: editingService.service,
                performedPeriod: {
                  start: editingService.startDate?.toISOString(),
                  end: editingService.terminationDate?.toISOString(),
                },
                outcome: editingService.response
                  ? { text: editingService.response }
                  : undefined,
              }
            : undefined
        }
      />

      <ConfirmCloseDialog
        opened={deleteConfirmOpened}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('therapeuticServices.confirmDelete.title', {
          defaultValue: 'Delete Therapeutic Service?',
        })}
        message={t('therapeuticServices.confirmDelete.message', {
          defaultValue:
            'Are you sure you want to delete this therapeutic service? This action cannot be undone.',
        })}
        confirmLabel={t('therapeuticServices.confirmDelete.confirm', {
          defaultValue: 'Delete',
        })}
        cancelLabel={t('therapeuticServices.confirmDelete.cancel', {
          defaultValue: 'Cancel',
        })}
        confirmColor="red"
      />
    </Stack>
  );
};
